import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import api from '../services/api';
import type {Quote, QuoteItem, QuoteItemInput, QuoteLineItem} from '../types/myaccount';
import {getQuoteDisplayId} from './quoteDisplay';

interface QuoteSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemPayload: QuoteItemInput | null;
    itemName?: string;
    title?: string;
    canCreateQuote?: boolean;
    canAddToExisting?: boolean;
    onSuccess?: () => void;
}

const formatMoney = (value: string | number | undefined): string => {
    const normalized = typeof value === 'string' ? parseFloat(value) : Number(value || 0);

    if (!Number.isFinite(normalized)) {
        return '$0.00';
    }

    return `$${normalized.toFixed(2)}`;
};

const formatQuoteStatus = (quote: Quote): string => {
    const rawStatus = String(quote.conversion_state || quote.status || '').trim();
    if (rawStatus === '') {
        return 'Draft';
    }

    return rawStatus
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
};

const isEditableQuote = (quote: Quote): boolean => {
    const conversionState = String(quote.conversion_state || '').trim();
    return conversionState === ''
        && quote.status !== 'completed'
        && quote.status !== 'converted'
        && quote.status !== 'quote-completed'
        && quote.status !== 'rejected';
};

const buildDefaultQuoteTitle = (itemName?: string): string => {
    const normalizedItemName = String(itemName || '').trim();
    const formattedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });

    if (normalizedItemName !== '') {
        return `${normalizedItemName} Quote - ${formattedDate}`;
    }

    return `New Quote - ${formattedDate}`;
};

const mapLineItemToUpdatePayload = (item: QuoteLineItem): QuoteItemInput => {
    const quantity = Number(item.quantity || 0);
    const price = parseFloat(String(item.unit_price || item.total || '0'));
    const discount = parseFloat(String(item.discount || '0'));
    const lineItemId = Number(item.id || 0);
    const variationId = Number(item.variation_id || 0);

    return {
        ...(lineItemId > 0 ? {line_item_id: lineItemId} : {}),
        product_id: Number(item.product_id || 0),
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        price: Number.isFinite(price) ? price : 0,
        discount: Number.isFinite(discount) ? discount : 0,
        ...(variationId > 0 ? {variation_id: variationId} : {}),
    };
};

const mapLegacyQuoteItemToUpdatePayload = (item: QuoteItem): QuoteItemInput => ({
    product_id: Number(item.product_id || 0),
    quantity: Number(item.quantity || 0) > 0 ? Number(item.quantity || 0) : 1,
    price: 0,
    ...(Number((item as { variation_id?: number }).variation_id || 0) > 0
        ? {variation_id: Number((item as { variation_id?: number }).variation_id || 0)}
        : {}),
});

const buildQuoteUpdateItems = (quote: Quote, newItem: QuoteItemInput): QuoteItemInput[] => {
    const existingItems = Array.isArray(quote.line_items) && quote.line_items.length > 0
        ? quote.line_items
            .map(mapLineItemToUpdatePayload)
            .filter((item) => item.product_id > 0)
        : Array.isArray(quote.items)
            ? quote.items
                .map(mapLegacyQuoteItemToUpdatePayload)
                .filter((item) => item.product_id > 0)
        : [];

    return [...existingItems, newItem];
};

const QuoteSelectorModal: React.FC<QuoteSelectorModalProps> = ({
    isOpen,
    onClose,
    itemPayload,
    itemName,
    title = 'Add to Quote',
    canCreateQuote = true,
    canAddToExisting = true,
    onSuccess,
}) => {
    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [newQuoteTitle, setNewQuoteTitle] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setErrorMessage('');
            setNewQuoteTitle('');
            return;
        }

        let cancelled = false;
        setLoading(canAddToExisting);
        setErrorMessage('');

        if (!canAddToExisting) {
            setQuotes([]);
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }

        api.invalidateCache('weka/v1/quotes');
        api.getQuotes()
            .then((response) => {
                if (cancelled) {
                    return;
                }

                const editableQuotes = Array.isArray(response)
                    ? response.filter(isEditableQuote)
                    : [];
                setQuotes(editableQuotes);
            })
            .catch((error) => {
                if (!cancelled) {
                    const message = error instanceof Error ? error.message : 'Failed to load quotes.';
                    setErrorMessage(message);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, canAddToExisting]);

    if (!isOpen) {
        return null;
    }

    const closeButtonClassName = `selector-modal__close${submitting ? ' is-disabled' : ''}`;
    const addToQuoteButtonClassName = `button weka-secondary-btn selector-modal__button selector-modal__button--secondary${submitting || !itemPayload ? ' is-disabled' : ''}`;
    const createQuoteButtonClassName = `button weka-primary-btn selector-modal__button selector-modal__button--primary${submitting || !itemPayload ? ' is-disabled' : ''}`;

    const handleAddToExisting = async (quoteId: number) => {
        if (!canAddToExisting || !itemPayload || submitting) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');

        try {
            const quote = await api.getQuote(quoteId);
            if (!quote || !isEditableQuote(quote)) {
                throw new Error('This quote can no longer be edited.');
            }

            const items = buildQuoteUpdateItems(quote, itemPayload);
            const response = await api.updateQuote(quoteId, {
                title: quote.title,
                notes: quote.notes || '',
                items,
            });

            api.invalidateCache('weka/v1/quotes');
            toast.success(response.message || 'Item added to quote.');
            onSuccess?.();
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to add item to quote.';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateQuote = async () => {
        if (!canCreateQuote || !itemPayload || submitting) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');

        try {
            const response = await api.createQuote({
                title: newQuoteTitle.trim() || buildDefaultQuoteTitle(itemName),
                items: [itemPayload],
                status: 'pending',
            });

            api.invalidateCache('weka/v1/quotes');
            toast.success(response.message || 'Quote created successfully.');
            onSuccess?.();
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create quote.';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="selector-modal-overlay" onClick={() => { if (!submitting) onClose(); }}>
            <div
                className="selector-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="quote-selector-title"
            >
                <div className="selector-modal__header">
                    <div>
                        <div id="quote-selector-title" className="selector-modal__title">{title}</div>
                        <p className="selector-modal__subtitle">
                            {itemName && itemName.trim() !== ''
                                ? `Choose where to save ${itemName}.`
                                : 'Choose an existing quote or create a new one.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        disabled={submitting}
                        onClick={onClose}
                        className={closeButtonClassName}
                    >
                        &times;
                    </button>
                </div>

                <div className="selector-modal__body">
                    {errorMessage !== '' && (
                        <div className="selector-modal__error" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    {canAddToExisting && (
                        <section className="selector-modal__section">
                            <div className="selector-modal__section-header">
                                <div className="selector-modal__section-title">Existing Quotes</div>
                                {loading && <span className="selector-modal__loading-text">Loading...</span>}
                            </div>

                            {!loading && quotes.length === 0 && (
                                <div className="selector-modal__empty-state">
                                    No open quotes found yet.
                                </div>
                            )}

                            <div className="selector-modal__list">
                                {quotes.map((quote) => (
                                    <div key={quote.id} className="selector-modal__list-item">
                                        <div className="selector-modal__list-meta">
                                            <p className="selector-modal__list-title">
                                                {String(quote.title || '').trim() || getQuoteDisplayId(quote)}
                                            </p>
                                            <p className="selector-modal__list-summary">
                                                {getQuoteDisplayId(quote)}
                                                {' | '}
                                                {formatQuoteStatus(quote)}
                                                {' | '}
                                                {formatMoney(quote.total)}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { void handleAddToExisting(quote.id); }}
                                            disabled={submitting || !itemPayload}
                                            className={addToQuoteButtonClassName}
                                        >
                                            {submitting ? 'Saving...' : 'Add to This Quote'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {canCreateQuote && (
                        <section className="selector-modal__section selector-modal__section--divided">
                            <div>
                                <div className="selector-modal__section-title">Create New Quote</div>
                                <p className="selector-modal__section-hint">Leave the name blank to use a generated quote title.</p>
                            </div>
                            <div className="selector-modal__input-row">
                                <input
                                    type="text"
                                    placeholder="Quote name"
                                    value={newQuoteTitle}
                                    onChange={(event) => setNewQuoteTitle(event.currentTarget.value)}
                                    disabled={submitting}
                                    className="selector-modal__input"
                                />
                                <button
                                    type="button"
                                    onClick={() => { void handleCreateQuote(); }}
                                    disabled={submitting || !itemPayload}
                                    className={createQuoteButtonClassName}
                                >
                                    {submitting ? 'Creating...' : 'Create and Add'}
                                </button>
                            </div>
                        </section>
                    )}

                    {!canAddToExisting && !canCreateQuote && (
                        <div className="selector-modal__empty-state">
                            Adding products to quotes is disabled for this page.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuoteSelectorModal;
